import { FC, useEffect, useMemo } from 'react';
import { WizardFormData, StepErrors } from '../types';
import { computeBucketPaths } from '../bucketPath';
import BucketTreePicker, { BucketNode } from '../../BucketTreePicker';
import SearchDropdown from '../../dropDownInput';
import Icons from '../../../../../../../assets/Icons';

export interface StepLocationProps {
    formData: WizardFormData;
    errors: StepErrors;
    locations: Array<{ label: string; subLabel: string }>;
    clients: Array<{ label: string; subLabel: string }>;
    supervisors: Array<{ label: string; subLabel: string }>;
    bucketTree: BucketNode[];
    bucketTreeLoading: boolean;
    bucketTreeError: string | null;
    selectedRole: string;
    onChange: (field: keyof WizardFormData, value: any) => void;
}

/**
 * Step 3 — Location, Client, Supervisor, and Bucket selection.
 */
export const Step3_Location: FC<StepLocationProps> = ({
    formData,
    errors,
    locations,
    clients,
    supervisors,
    bucketTree,
    bucketTreeLoading,
    bucketTreeError,
    selectedRole,
    onChange,
}) => {
    // Always show location and client fields in text mode.
    // Supervisor is always shown but optional.
    const showSupervisor = true;

    const bucketPaths = useMemo(
        () =>
            computeBucketPaths(
                formData.locations,
                formData.clients,
                formData.supervisors,
                selectedRole,
                formData.email
            ),
        [formData.locations, formData.clients, formData.supervisors, selectedRole, formData.email]
    );

    // Auto-map text field selections to bucket tree nodes
    // Walks the tree to find nodes matching the computed bucket paths
    useEffect(() => {
        if (bucketPaths.length === 0 || bucketTree.length === 0) return;

        // Build a lookup: bucket_id → node, and parent_bucket → children
        const nodeMap = new Map<number, BucketNode>();
        const childrenMap = new Map<number | null, BucketNode[]>();
        for (const node of bucketTree) {
            nodeMap.set(node.bucket_id, node);
            const parentKey = node.parent_bucket;
            if (!childrenMap.has(parentKey)) childrenMap.set(parentKey, []);
            childrenMap.get(parentKey)!.push(node);
        }

        // Normalize a string for matching: lowercase, spaces→underscores
        const normalize = (s: string) => s.trim().toLowerCase().replace(/\s+/g, '_');

        // For each computed bucket path, walk the tree to find the deepest matching node
        const matchedIds: number[] = [];
        for (const path of bucketPaths) {
            // path format: "itel/location/client/supervisor" or "itel/location/client"
            const segments = path.split('/'); // e.g., ["itel", "jamaica", "telyrx", "jane_doe"]

            let currentNodes = childrenMap.get(null) ?? []; // root nodes
            let matchedNode: BucketNode | null = null;

            for (const segment of segments) {
                const found = currentNodes.find((n) => normalize(n.name) === segment);
                if (found) {
                    matchedNode = found;
                    currentNodes = childrenMap.get(found.bucket_id) ?? [];
                } else {
                    break;
                }
            }

            // Select the deepest matched node (leaf of the path)
            if (matchedNode && !matchedIds.includes(matchedNode.bucket_id)) {
                matchedIds.push(matchedNode.bucket_id);
            }
        }

        if (matchedIds.length > 0) {
            onChange('selectedBucketIds', matchedIds);
        }
    }, [bucketPaths, bucketTree]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleModeChange = (mode: 'text' | 'tree') => {
        onChange('bucketSelectionMode', mode);
    };

    const handleLocationSelect = (item: { label: string; subLabel: string }, _id: string) => {
        if (item.label && item.label.trim() && !formData.locations.includes(item.label)) {
            onChange('locations', [...formData.locations, item.label]);
        }
    };

    const handleClientSelect = (item: { label: string; subLabel: string }, _id: string) => {
        if (item.label && item.label.trim() && !formData.clients.includes(item.label)) {
            onChange('clients', [...formData.clients, item.label]);
        }
    };

    const handleSupervisorSelect = (item: { label: string; subLabel: string }, _id: string) => {
        if (item.label && item.label.trim() && !formData.supervisors.includes(item.label)) {
            onChange('supervisors', [...formData.supervisors, item.label]);
        }
    };

    const handleBucketSelectionChange = (bucketIds: number[]) => {
        onChange('selectedBucketIds', bucketIds);

        // Reverse-map: walk up the tree from each selected node to extract location/client/supervisor
        if (bucketIds.length > 0 && bucketTree.length > 0) {
            const nodeMap = new Map<number, BucketNode>();
            for (const node of bucketTree) {
                nodeMap.set(node.bucket_id, node);
            }

            const newLocations = new Set<string>();
            const newClients = new Set<string>();
            const newSupervisors = new Set<string>();

            for (const id of bucketIds) {
                // Walk up from selected node to root, collecting path segments
                const path: string[] = [];
                let current = nodeMap.get(id);
                while (current) {
                    path.unshift(current.name);
                    current = current.parent_bucket !== null ? nodeMap.get(current.parent_bucket) : undefined;
                }
                // Expected path: [itel, location, client, supervisor] or [itel, location, client]
                // Index: 0=itel, 1=location, 2=client, 3=supervisor
                if (path.length >= 2) newLocations.add(path[1]);
                if (path.length >= 3) newClients.add(path[2]);
                if (path.length >= 4) newSupervisors.add(path[3]);
            }

            if (newLocations.size > 0) onChange('locations', Array.from(newLocations));
            if (newClients.size > 0) onChange('clients', Array.from(newClients));
            if (newSupervisors.size > 0) onChange('supervisors', Array.from(newSupervisors));
        }
    };

    const isTextMode = formData.bucketSelectionMode === 'text';
    const isTreeMode = formData.bucketSelectionMode === 'tree';

    return (
        <div className="wizard-step wizard-step-location">
            {/* Mode Toggle — uses existing app styles from BucketTreePicker.scss */}
            <div className="bucket-mode-toggle" role="group" aria-label="Bucket selection mode">
                <button
                    type="button"
                    className={`bucket-mode-btn ${isTextMode ? 'active' : ''}`}
                    onClick={() => handleModeChange('text')}
                    aria-pressed={isTextMode}
                >
                    Text Fields
                </button>
                <button
                    type="button"
                    className={`bucket-mode-btn ${isTreeMode ? 'active' : ''}`}
                    onClick={() => handleModeChange('tree')}
                    aria-pressed={isTreeMode}
                >
                    Select Bucket
                </button>
            </div>

            {/* Text Fields Mode */}
            {isTextMode && (
                <div className="wizard-text-fields-mode">
                    <div className="wizard-field">
                        <div className="user_list_title">Select a Location:</div>
                        <SearchDropdown
                            data={locations}
                            onSelect={handleLocationSelect}
                            selected=""
                            id="location"
                            editable={false}
                        />
                        {formData.locations.length > 0 && (
                            <div style={{ display: 'flex' }}>
                                {formData.locations.map((loc, idx) => (
                                    <div key={idx} className="item_option_selected" style={{ display: 'flex' }}>
                                        {loc}
                                        <div style={{ cursor: 'pointer' }} onClick={() =>
                                            onChange('locations', formData.locations.filter((_, i) => i !== idx))
                                        }>
                                            <Icons.Trash />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="wizard-field">
                        <div className="user_list_title">Select a Client:</div>
                        <SearchDropdown
                            data={clients}
                            onSelect={handleClientSelect}
                            selected=""
                            id="client"
                            editable={true}
                        />
                        {formData.clients.length > 0 && (
                            <div style={{ display: 'flex' }}>
                                {formData.clients.map((client, idx) => (
                                    <div key={idx} className="item_option_selected" style={{ display: 'flex' }}>
                                        {client}
                                        <div style={{ cursor: 'pointer' }} onClick={() =>
                                            onChange('clients', formData.clients.filter((_, i) => i !== idx))
                                        }>
                                            <Icons.Trash />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {showSupervisor && (
                        <div className="wizard-field">
                            <div className="user_list_title">Select a Supervisor (optional):</div>
                            <SearchDropdown
                                data={supervisors}
                                onSelect={handleSupervisorSelect}
                                selected=""
                                id="supervisor"
                                editable={true}
                            />
                            {formData.supervisors.length > 0 && (
                                <div style={{ display: 'flex' }}>
                                    {formData.supervisors.map((sup, idx) => (
                                        <div key={idx} className="item_option_selected" style={{ display: 'flex' }}>
                                            {sup}
                                            <div style={{ cursor: 'pointer' }} onClick={() =>
                                                onChange('supervisors', formData.supervisors.filter((_, i) => i !== idx))
                                            }>
                                                <Icons.Trash />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Tree Mode */}
            {isTreeMode && (
                <div className="wizard-tree-mode">
                    {bucketTreeError ? (
                        <div className="wizard-tree-error" role="alert">
                            Bucket tree is unavailable: {bucketTreeError}
                        </div>
                    ) : (
                        <BucketTreePicker
                            buckets={bucketTree}
                            selectedBucketIds={formData.selectedBucketIds}
                            onSelectionChange={handleBucketSelectionChange}
                            loading={bucketTreeLoading}
                        />
                    )}
                </div>
            )}

            {/* Bucket Path Preview — shows in both modes */}
            {bucketPaths.length > 0 && (
                <div className="wizard-bucket-path-preview">
                    <div className="user_list_title">Bucket Hierarchy Preview:</div>
                    <p style={{ fontSize: '12px', color: '#666', margin: '4px 0 8px' }}>
                        This is the path where the user will be placed: itel / location / client / supervisor
                    </p>
                    <ul className="wizard-bucket-paths-list">
                        {bucketPaths.map((path, idx) => {
                            const segments = path.split('/');
                            return (
                                <li key={idx} className="wizard-bucket-path-item" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    {segments.map((seg, sIdx) => (
                                        <span key={sIdx} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                            <span style={{
                                                padding: '2px 6px',
                                                borderRadius: '3px',
                                                fontSize: '12px',
                                                backgroundColor: sIdx === 0 ? '#e8e8e8' : sIdx === 1 ? '#dbeafe' : sIdx === 2 ? '#dcfce7' : '#fef3c7',
                                                color: '#333'
                                            }}>
                                                {seg}
                                            </span>
                                            {sIdx < segments.length - 1 && <span style={{ color: '#999' }}>/</span>}
                                        </span>
                                    ))}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}

            {/* Tree mode: show supervisor-appended paths when supervisor is selected */}
            {isTreeMode && formData.selectedBucketIds.length > 0 && formData.supervisors.length > 0 && bucketPaths.length === 0 && (
                <div className="wizard-bucket-path-preview">
                    <div className="user_list_title">Final Assignment Preview:</div>
                    <p style={{ fontSize: '12px', color: '#666', margin: '4px 0 8px' }}>
                        Selected buckets will have supervisor sub-buckets created beneath them.
                    </p>
                    <ul className="wizard-bucket-paths-list">
                        {formData.supervisors.map((sup, idx) => (
                            <li key={idx} className="wizard-bucket-path-item">
                                [selected bucket] / <span style={{ padding: '2px 6px', borderRadius: '3px', backgroundColor: '#fef3c7', fontSize: '12px' }}>{sup.toLowerCase().replace(/\s+/g, '_')}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Inline Error */}
            {errors.bucket && (
                <span
                    id="bucket-error"
                    className="wizard-field-error"
                    role="alert"
                    style={{ color: 'red', fontSize: '12px', marginTop: '8px', display: 'block' }}
                >
                    {errors.bucket}
                </span>
            )}
        </div>
    );
};

export default Step3_Location;
